import { AudioOutlined, CloseOutlined, InboxOutlined } from "@ant-design/icons";
import { Button, Col, Typography, Upload, UploadProps, message } from "antd";
import Dragger from "antd/es/upload/Dragger";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import styles from "./createnotemodal.module.scss";

const MAX_AUDIO_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB limit for audio files

const FileUploader = ({
  audioFileRef,
}: {
  audioFileRef: React.MutableRefObject<any>;
}) => {
  const dispatch = useAppDispatch();
  const componentName = "createnotemodal";

  const { recordingDetail } = useAppSelector((state) => state.recordings);

  const isAudioFile = (file: File): boolean => {
    // Check MIME type
    if (file.type && !file.type.startsWith("audio/")) {
      return false;
    }

    // Additional check for common audio file extensions if MIME type is not set
    const audioExtensions = [".mp3", ".m4a", ".wav", ".webm", ".ogg", ".aac", ".flac", ".opus"];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

    if (!file.type && !audioExtensions.includes(fileExtension)) {
      return false;
    }

    return true;
  };

  const audioProps: UploadProps = {
    name: "file",
    accept: "audio/*",
    multiple: false,
    showUploadList: false,
    onChange(info) {
      //@ts-ignore
      audioFileRef.current = info.file;
      return false;
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
    beforeUpload(file) {
      // Validate it's an audio file
      if (!isAudioFile(file)) {
        message.error("Only audio files are allowed. Supported formats: MP3, M4A, WAV, WEBM, OGG");
        return Upload.LIST_IGNORE;
      }

      // Check audio file size limit (100MB)
      if (file.size > MAX_AUDIO_UPLOAD_SIZE) {
        message.error(`Audio file exceeds 100MB limit. File size: ${formatFileSize(file.size)}`);
        return Upload.LIST_IGNORE;
      }

      dispatch(setRecordingDetail({ ...recordingDetail, fileList: [file] }));
      return false;
    },
    fileList: recordingDetail.fileList || [],
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  const handleRemoveAudioFile = () => {
    audioFileRef.current = null;
    dispatch(setRecordingDetail({ ...recordingDetail, fileList: [] }));
  };

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`} style={{ marginTop: "15px" }}>
      <div
        style={{
          padding: "12px",
          background: "#e6f4ff",
          border: "1px solid #91caff",
          borderRadius: "4px",
          marginBottom: "12px",
        }}
      >
        <Typography.Text style={{ color: "#0958d9", fontSize: "14px" }}>
          Upload your session recording to generate clinical notes.
        </Typography.Text>
      </div>

      {/* Show selected audio file */}
      {recordingDetail.fileList && recordingDetail.fileList.length > 0 && (
        <div style={{ marginBottom: "12px" }}>
          {recordingDetail.fileList.map((file: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                background: "#e6f7ff",
                borderRadius: "4px",
                marginBottom: "8px",
                border: "1px dashed #91d5ff",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                <AudioOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text ellipsis={{ tooltip: file.name }} style={{ display: "block", fontWeight: 500 }}>
                    {file.name}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                    {formatFileSize(file.size)} • Will upload when Generate Note is clicked
                  </Typography.Text>
                </div>
              </div>
              <Button
                type="text"
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={handleRemoveAudioFile}
                style={{ marginLeft: "8px" }}
              />
            </div>
          ))}
        </div>
      )}

      <Dragger {...audioProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag audio file to this area to upload</p>
        <p className="ant-upload-hint">
          Single audio file upload. Supported formats: MP3, M4A, WAV, WEBM, OGG. Max size: 100MB
        </p>
      </Dragger>
    </Col>
  );
};

export default FileUploader;
